import { Box } from '@bridebook/ui';
import AppListeners from '@bridebook/web/components/app/app-listeners';
import Header from '@bridebook/web/components/app/header/header';
import Footer from '@bridebook/web/components/bbcommon/footer/footer';
import MobileNavigation from '@bridebook/web/components/mobile-app/mobile-navigation';
import SettingsFooter from '@bridebook/web/components/settings/settings-footer/settings-footer';
import ComponentLevelErrorBoundary from '@bridebook/web/lib/app/component-level-error-boundary';
import { getDevice, getScreenType } from '@bridebook/web/lib/app/selectors';
import { IPageSettings } from '@bridebook/web/lib/page-settings-context';
import { getShowReviewsPage } from '@bridebook/web/lib/supplier/selectors';
import { getMobileNavShown } from '@bridebook/web/lib/ui/selectors';
import { useHideFooterValue } from '@bridebook/web/lib/ui/hooks/use-hide-footer';
import { showFooter } from '@bridebook/web/lib/ui/show-footer';
import { selectCurrentUser } from '@bridebook/web/lib/users/selectors';
import { useSelector } from '@bridebook/web/lib/utils';
import { usePathWithoutMarket } from '@bridebook/web/lib/utils/url';
import { useRouter } from 'next/router';
import { memo, PropsWithChildren, ReactNode, useMemo } from 'react';
import TopBarProtected from './app/top-bar/top-bar-protected';
import componentStyles from './page-content.style';

interface IProps {
  childrenContentWrap?: ReactNode;
  contentOffset?: {
    desktop?: number;
    tablet?: number;
    mobile?: number;
  };
  customHeader?: ReactNode;
  disableSafeAreaInsetTop?: boolean;
  hideFooter?: boolean;
  hideFooterTop?: boolean;
  isSettingsCordova?: boolean;
  pageSettings: IPageSettings;
  reservedStickySpace?: boolean;
  showHeader: boolean;
  stickyHeader?: boolean;
  subHeaderContent?: ReactNode;
}

const PageContentComponent = ({
  children,
  childrenContentWrap = null,
  contentOffset = {},
  customHeader,
  disableSafeAreaInsetTop = false,
  hideFooter,
  hideFooterTop,
  isSettingsCordova,
  pageSettings,
  reservedStickySpace,
  showHeader,
  stickyHeader,
  subHeaderContent,
}: PropsWithChildren<IProps>) => {
  const pathWithoutMarket = usePathWithoutMarket();
  const isFooterHiddenByHook = useHideFooterValue();
  const device = useSelector(getDevice);
  const user = useSelector(selectCurrentUser);
  const showReviewsPage = useSelector(getShowReviewsPage);
  const { isMobile, isTablet } = useSelector(getScreenType);
  const mobileNavShown = useSelector(getMobileNavShown);
  const isAdviceMobile = isMobile && pathWithoutMarket.includes('advice');
  const isBookmarkMobile = isMobile && pathWithoutMarket.includes('bookmark');
  const router = useRouter();

  const isFooterHidden = hideFooter || isFooterHiddenByHook;

  const pageKey = useMemo(() => {
    const pathname = router.asPath.split('?')[0];
    if (pathname === '/') {
      return 'index';
    }
    return pathWithoutMarket.split('?')[0].split('/')[1]; // for example 'home', 'guestlist' or 'inbox'
  }, [pathWithoutMarket, router.asPath]);

  const contentOffsetWithDefaults = useMemo(
    () => ({
      desktop: 0,
      tablet: 0,
      mobile: 0,
      ...contentOffset,
    }),
    [contentOffset],
  );

  const styles = componentStyles({
    backgroundColor: pageSettings.backgroundColor,
    isMobile,
    isTablet,
    mobileNavShown,
    reservedStickySpace,
    bottomSafeArea: pathWithoutMarket !== '/home',
    stickyHeader,
    contentOffset: contentOffsetWithDefaults,
    disableSafeAreaInsetTop,
  });

  return (
    <Box style={styles.pageContent} data-name="page" key={pageKey}>
      <AppListeners />
      <Box style={styles.wrapper}>
        {showHeader ? customHeader || <TopBarProtected /> : null}
        <Box style={styles.contentWrap}>
          {showHeader && (
            <>
              <Header />
              {subHeaderContent}
            </>
          )}
          {childrenContentWrap}
          <Box style={styles.childrenWrap}>
            <ComponentLevelErrorBoundary component="PageChildren">
              {children}
            </ComponentLevelErrorBoundary>
          </Box>
          {!isFooterHidden &&
            showFooter({
              pathname: pathWithoutMarket,
              device,
              user,
              showReviewsPage,
              isTablet,
              isMobile,
            }) && <Footer hideTop={hideFooterTop} />}
          {isSettingsCordova && !isAdviceMobile && !isBookmarkMobile && <SettingsFooter />}
        </Box>
      </Box>
      <MobileNavigation />
    </Box>
  );
};

export const PageContent = memo(PageContentComponent);
